import './card-info-list.scss';

import { Tooltip, message } from 'antd';
import cn from 'classnames';

import { LoadingOutlined } from '@/media/antd-icons';
import CopyIcon from '@/media/icons/copy.svg?react';
import DownloadIcon from '@/media/icons/download.svg?react';

import { LocalizedLink } from '../localized-link';

export interface IItem {
  label: string;
  value: string;
  valueColor?: 'red' | 'yellow' | 'green';
  copyable?: boolean;
  onClick?: () => void;
  download?:
    | {
        onDownload: () => void;
        isLoading: boolean;
      }
    | {
        href: string;
      };
  href?: string;
  isDefaultLinkComponent?: boolean;
  isUnderlined?: boolean;
  defaultDirection?: 'horizontal' | 'vertical';
  classes?: {
    block?: string;
    label?: string;
    value?: string;
  };
}

const CardInfoItem: React.FC<IItem> = ({
  label,
  value,
  valueColor,
  copyable,
  onClick,
  download,
  href,
  isDefaultLinkComponent,
  isUnderlined,
  defaultDirection = 'horizontal',
  classes
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  const handleCopy = (value: string) => {
    messageApi.open({
      type: 'success',
      content: 'Данные успешно скопированы!'
    });
    navigator.clipboard.writeText(value);
  };

  const valueClass = ['card-info-list__value'];
  if (valueColor) valueClass.push(`card-info-list__value--${valueColor}`);
  if (isUnderlined) valueClass.push('card-info-list__value--underlined');
  if (classes?.value) valueClass.push(classes.value);

  const downloadBlock = download ? (
    'href' in download ? (
      <a
        className={cn(
          'card-info-list__download card-info-list__value',
          classes?.value
        )}
        href={download.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {value || '—'}
        <DownloadIcon />
      </a>
    ) : (
      <button
        className={cn(
          'card-info-list__download card-info-list__value',
          classes?.value
        )}
        onClick={download.onDownload}
        disabled={download.isLoading}
      >
        {value || '—'}
        {download.isLoading ? <LoadingOutlined /> : <DownloadIcon />}
      </button>
    )
  ) : undefined;

  const hrefBlock = href ? (
    isDefaultLinkComponent ? (
      <a
        className={cn('card-info-list__value', classes?.value)}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {value || '—'}
      </a>
    ) : (
      <LocalizedLink
        to={href}
        className={cn('card-info-list__value', classes?.value)}
        onClick={(e) => e.stopPropagation()}
      >
        {value || '—'}
      </LocalizedLink>
    )
  ) : undefined;

  return (
    <div
      key={label}
      className={cn('card-info-list__block', classes?.block, {
        'card-info-list__block--clickable': copyable || onClick,
        'card-info-list__block--vertical': defaultDirection === 'vertical'
      })}
      onClick={
        onClick ? onClick : copyable ? () => handleCopy(value) : undefined
      }
    >
      {contextHolder}
      <span className={cn('card-info-list__label', classes?.label)}>
        {label}
      </span>
      {downloadBlock ? (
        downloadBlock
      ) : (
        <span className="card-info-list__value-wrapper">
          {hrefBlock ? (
            hrefBlock
          ) : (
            <span className={valueClass.join(' ')}>{value || '—'}</span>
          )}
          {copyable && (
            <Tooltip title={`Скопировать "${label.replace(':', '')}"`}>
              <button className="card-info-list__copy">
                <CopyIcon />
              </button>
            </Tooltip>
          )}
        </span>
      )}
    </div>
  );
};

interface IProps {
  list: Array<IItem[]>;
  selfPlaced?: boolean;
}

const CardInfoList: React.FC<IProps> & { Item: React.FC<IItem> } = ({
  list,
  selfPlaced = false
}) => {
  return (
    <div
      className={cn('card-info-list', {
        'card-info-list--self-placed': selfPlaced
      })}
    >
      {list.map((items, index) => (
        <div key={index} className="card-info-list__info-wrapper">
          {items.map((item) => (
            <CardInfoItem key={item.label} {...item} />
          ))}
        </div>
      ))}
    </div>
  );
};

CardInfoList.Item = CardInfoItem;

export default CardInfoList;
